/**
 * Strategy Token auto-reservation rules (frontend-only).
 *
 * Every NEW External strategy (TRADINGVIEW, AMIBROKER, PYTHON — provider-
 * independent) automatically reserves one Strategy Token via the existing
 * strategiesApi.reserveToken POST. Internal and Manual strategies never
 * auto-reserve. Edit mode never reserves a replacement for the saved token.
 *
 * These pure helpers are the single expression of those rules, extracted so
 * the StrategyBuilder effect/handler/retry paths share them and so the rules
 * are unit-testable with the repo's vitest setup. No backend contract change.
 */

/**
 * Provider-independent eligibility: only new (non-edit) EXTERNAL strategies
 * auto-reserve a token. Extra fields (e.g. externalSourceType) are ignored by
 * design — provider selection must not affect token generation.
 */
export function isAutoTokenReservationEligible({ isEdit, strategySource }) {
  return !isEdit && strategySource === 'EXTERNAL';
}

/**
 * Full guard set for the automatic attempt: eligible, no token already
 * reserved, no reservation already in progress, and the endpoint not already
 * marked unsupported. Composed by the StrategyBuilder mount effect.
 */
export function shouldAttemptAutoReservation({
  isEdit,
  strategySource,
  reservedStrategyToken,
  isReservingToken,
  tokenReservationUnsupported,
}) {
  return isAutoTokenReservationEligible({ isEdit, strategySource })
    && !reservedStrategyToken
    && !isReservingToken
    && !tokenReservationUnsupported;
}

/** Exact extraction of the token field from the reserve-token response. */
export function extractReservedToken(resp) {
  return resp?.strategy_token || resp?.webhook_token || '';
}

/** 404/405 means the reservation endpoint is unavailable in this deployment. */
export function isTokenReservationUnsupportedError(err) {
  const status = err?.status || err?.response?.status;
  return status === 404 || status === 405;
}
